import React from 'react';
import { Music, Piano, Mic } from 'lucide-react';
import { artistInfo } from '../mock';

export const About = () => {
  return (
    <section id="about" className="py-24 bg-gray-900 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-gold-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-50 mb-4">
              About Brooksgordon
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-gold-500 mx-auto"></div>
          </div>

          {/* Icon Features */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center space-y-4 p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-teal-900/20 hover:border-teal-700/50 transition-all duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-900/50 rounded-full">
                <Mic className="text-gold-400" size={32} />
              </div>
              <h2 className="text-xl font-semibold text-teal-100">Vocalist</h2>
              <p className="text-teal-300 text-sm">
                Soulful sound compared to Luther Vandross, Brian McKnight, and Will Downing
              </p>
            </div>

            <div className="text-center space-y-4 p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-teal-900/20 hover:border-teal-700/50 transition-all duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-900/50 rounded-full">
                <Piano className="text-gold-400" size={32} />
              </div>
              <h2 className="text-xl font-semibold text-teal-100">Pianist</h2>
              <p className="text-teal-300 text-sm">
                Classically trained with over 30 years of dedicated study and performance
              </p>
            </div>

            <div className="text-center space-y-4 p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-teal-900/20 hover:border-teal-700/50 transition-all duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-900/50 rounded-full">
                <Music className="text-gold-400" size={32} />
              </div>
              <h2 className="text-xl font-semibold text-teal-100">Songwriter</h2>
              <p className="text-teal-300 text-sm">
                Creator of heartfelt melodies channeling a lifetime of musical experience
              </p>
            </div>
          </div>

          {/* In The Studio */}
          <div className="text-center mb-8">
            <span className="text-gold-400 uppercase tracking-[0.3em] text-xs font-semibold">In The Studio</span>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="relative rounded-2xl overflow-hidden group aspect-[4/3]">
              <img
                src="https://customer-assets.emergentagent.com/job_d063d7a9-eda0-44cf-9fcf-715c92eeba27/artifacts/zgyoarm5_image~4.jpg"
                alt="Brooksgordon recording vocals in the studio"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-gold-400 uppercase tracking-widest text-xs font-semibold">Vocal Booth</span>
                <h2 className="text-2xl font-bold text-white mt-1">Where Every Take Begins</h2>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group aspect-[4/3]">
              <img
                src="https://customer-assets.emergentagent.com/job_d063d7a9-eda0-44cf-9fcf-715c92eeba27/artifacts/wpnj3h13_image~3.jpg"
                alt="Brooksgordon at a grand piano with a vintage microphone"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-gold-400 uppercase tracking-widest text-xs font-semibold">Grand Piano</span>
                <h2 className="text-2xl font-bold text-white mt-1">Composing in Real Time</h2>
              </div>
            </div>
          </div>

          {/* Bio Content */}
          <div id="bio" className="bg-black/40 backdrop-blur-sm border border-teal-900/20 rounded-2xl p-8 md:p-12 scroll-mt-28">
            <div className="prose prose-lg prose-invert max-w-none">
              {artistInfo.bio.split('\n\n').map((paragraph, index) => (
                <p key={`bio-${index}`} className="text-teal-100 leading-relaxed mb-6 text-lg">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Signature or quote */}
            <div className="mt-12 pt-8 border-t border-teal-900/30">
              {/* Availability & Licensing */}
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                <div className="bg-teal-900/20 border border-teal-800/30 rounded-xl p-6">
                  <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-widest mb-3">Available For Booking</h3>
                  <ul className="space-y-2 text-teal-200 text-sm">
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gold-500 rounded-full"></span> Atlanta Metropolitan Area</li>
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gold-500 rounded-full"></span> The Low Country Region</li>
                    <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gold-500 rounded-full"></span> International Engagements</li>
                  </ul>
                </div>
                <div className="bg-teal-900/20 border border-teal-800/30 rounded-xl p-6">
                  <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-widest mb-3">Sync Licensing</h3>
                  <p className="text-teal-200 text-sm mb-4">
                    Music from The Gordon Principle is available for film, television, and commercial media licensing.
                  </p>
                  <a
                    href="mailto:Ishmael.braggs@brooksgordon.com?subject=Sync%20Licensing%20Inquiry%20-%20The%20Gordon%20Principle"
                    className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-black px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:scale-105"
                  >
                    Inquire About Licensing
                  </a>
                </div>
              </div>

              <p className="text-2xl md:text-3xl font-light text-gold-400 italic text-center">
                "A compelling and dynamic force in music, poised for an exciting new chapter."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
